<template>
  <div
    :class="[
      'group/collapsible-panel',
      { 'bg-gray-10': !isCollapsed && collapseToggleFull, 'hover:bg-gray-10 mb-2 rounded-md': collapseToggleFull },
      panelClass,
      !isCollapsed && panelExpandedClass,
    ]"
  >
    <button
      :class="[
        'text-gray-60 group/collapsible-panel-button',
        { 'cursor-default': disabled, 'text-gray-80': collapseToggleFull, 'w-full': fullWidth || collapseToggleFull },
        hasAppendix && 'bg-zblue-10 hover:bg-zblue-10',
        titleLink ? 'px-0 py-0' : 'px-2 py-2',
      ]"
      type="button"
      :aria-expanded="!isCollapsed"
      @click="collapse"
    >
      <template v-if="$slots.appendix"><slot name="appendix" /></template>
      <div :class="['flex w-full items-center gap-2', { 'justify-between': collapseToggleFull }]">
        <slot name="trigger">
          <span
            v-if="$slots.icon || $slots.title"
            :class="[
              collapseToggleFull ? 'flex items-center gap-2.5 overflow-hidden text-xs' : 'text-xxs',
              titleLink ? 'content-link !inline-flex !items-center !gap-2 !text-sm' : 'text-default font-bold',
            ]"
          >
            <span v-if="$slots.icon" class="flex flex-shrink-0 items-center">
              <slot name="icon" />
            </span>
            <template v-if="$slots.title">
              <slot name="title" />
            </template>
          </span>
          <div v-if="$slots.customContent" class="flex w-full items-center justify-between overflow-x-hidden text-left">
            <slot name="customContent" />
          </div>
          <div class="flex items-center gap-2">
            <slot name="action-menu" :panel-collapsed="isCollapsed" />
            <span
              v-if="!disabled && !hideChevronIcon"
              :class="[
                'flex items-center justify-center p-1',
                {
                  'rotate-180': !isCollapsed,
                  'rounded-full leading-[0] mix-blend-multiply': collapseToggleFull,
                },
              ]"
            >
              <chevron-down-icon :class="titleLink ? 'text-zblue-60 icon-m' : 'text-gray-60 icon-s'" />
            </span>
          </div>
        </slot>
      </div>
      <template v-if="$slots.dispute">
        <slot name="dispute" />
      </template>
      <template v-if="$slots.evaluation">
        <slot name="evaluation" />
      </template>
    </button>

    <div
      :class="[
        'opacity-1 flex max-h-[9999999999px] flex-col pb-2 transition-all',
        isCollapsed ? 'mt-0 !max-h-0 overflow-hidden !pb-0 opacity-0' : classList,
        titleLink ? 'duration-[100ms]' : 'duration-[600ms]',
      ]"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChevronDownIcon } from '@klausapp/ui-kit/icons'
import { ref, computed, watch } from 'vue'

const props = defineProps<{
  collapsed?: boolean
  disabled?: boolean
  collapseToggleFull?: boolean
  contentClass?: string
  panelClass?: string
  panelExpandedClass?: string
  hasAppendix?: boolean
  hideChevronIcon?: boolean
  fullWidth?: boolean
  titleLink?: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle'): void
}>()

const isCollapsed = ref(props.collapsed)

const classList = computed(() => {
  if (props.contentClass) return props.contentClass
  return props.collapseToggleFull ? 'mt-2' : 'mt-3'
})

function collapse() {
  if (props.disabled) return

  isCollapsed.value = !isCollapsed.value
  emit('toggle')
}

watch(
  () => props.collapsed,
  (value) => (isCollapsed.value = value),
)
</script>
